import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SeoActions } from '@store/reducers/seo/actions'
import type { SeoFormValues } from '@store/reducers/seo/types'
import type { AppState } from '@store/types'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isValidUrl } from '@/helpers'
import { useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { SEO_TAGS_PERMISSION } from '@/permissions/project/definitions'
import { validateSlug } from './utils'

type FieldValue = {
  name: keyof SeoFormValues
  value: string | { id: string; value: string }
}

export const useSidebarForm = (nodeId: ID) => {
  const dispatch = useDispatch()
  const [slugError, setSlugError] = useState({
    message: 'is not valid',
    isValid: true,
  })

  const form = useSelector<AppState, SeoFormValues>(({ seoForm }) => seoForm)
  const isReadOnlyByAccess = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )
  const canEditContent = useSelector(
    migrationProjectSelector.getProjectAccessCanEditContent
  )

  const readOnly = isReadOnlyByAccess && !canEditContent

  const { hasAccess } = useProjectAccess(SEO_TAGS_PERMISSION.EDIT)
  const artboardDisabled = useArtBoardDisabled()

  const canFormEdit = hasAccess && !artboardDisabled

  const handleOnChange = ({ name, value }: FieldValue) => {
    if (name === 'slug') {
      setSlugError((prev) => ({
        ...prev,
        isValid: validateSlug(value as string),
      }))
    }

    dispatch(
      SeoActions.change({ id: nodeId, form: { ...form, [name]: value } })
    )
  }

  const handleUrlBlur = () => {
    const url = form.url.trim()

    if (isValidUrl(url) && !/^(https?|ftp):\/\//i.test(url)) {
      handleOnChange({ name: 'url', value: `https://${url}` })
    }
  }

  return {
    form,
    slugError,
    readOnly,
    hasAccess,
    canFormEdit,
    handleOnChange,
    handleUrlBlur,
  }
}
